// Lightweight, fail-safe client analytics. Batches events and flushes them
// asynchronously so tracking NEVER blocks or breaks any user action. If the
// network fails, events are simply dropped — the app keeps working.
import { API } from "@/lib/api";

const FLUSH_MS = 4000;
const MAX_BATCH = 25;
let _queue = [];
let _timer = null;

// Map a router pathname to a friendly page name for readable analytics.
const PAGE_NAMES = [
  [/^\/$/, "Home"],
  [/^\/login/, "Login"],
  [/^\/pricing/, "Pricing"],
  [/^\/contact/, "Contact"],
  [/^\/profile/, "Profile"],
  [/^\/admin/, "Admin"],
  [/^\/tools\/instagram-post-maker/, "Instagram Post Maker"],
  [/^\/tools\/cv-creator/, "CV Creator"],
  [/^\/tools\/cover-letter/, "Cover Letter Writer"],
  [/^\/tools/, "Tools Hub"],
  [/^\/instagram-post-maker/, "Instagram Post Maker"],
  [/^\/editor/, "Editor"],
  [/^\/portfolio/, "Portfolio"],
  [/^\/templates/, "Template Browse"],
];

export function pageName(pathname) {
  for (const [rx, name] of PAGE_NAMES) if (rx.test(pathname)) return name;
  return pathname;
}

function _schedule() {
  if (_timer) return;
  _timer = setTimeout(flush, FLUSH_MS);
}

export function flush(useBeacon = false) {
  if (_timer) { clearTimeout(_timer); _timer = null; }
  if (!_queue.length) return;
  const batch = _queue.slice(0, 100);
  _queue = _queue.slice(100);
  const body = JSON.stringify({ events: batch });
  try {
    if (useBeacon && navigator.sendBeacon) {
      navigator.sendBeacon(`${API}/analytics/events`, new Blob([body], { type: "application/json" }));
    } else {
      fetch(`${API}/analytics/events`, {
        method: "POST", credentials: "include", keepalive: true,
        headers: { "Content-Type": "application/json" }, body,
      }).catch(() => {});
    }
  } catch { /* never throw */ }
  if (_queue.length) _schedule();
}

// Core API: trackEvent({ eventType, page, feature, action, target, metadata })
export function trackEvent(ev) {
  try {
    if (!ev || !ev.eventType) return;
    _queue.push({
      eventType: String(ev.eventType),
      page: ev.page || (typeof window !== "undefined" ? pageName(window.location.pathname) : undefined),
      feature: ev.feature,
      action: ev.action,
      target: ev.target,
      metadata: ev.metadata,
      ts: new Date().toISOString(),
    });
    if (_queue.length >= MAX_BATCH) flush();
    else _schedule();
  } catch { /* never throw */ }
}

export function trackPageView(pathname) {
  trackEvent({ eventType: "page_view", page: pageName(pathname), action: `Viewed ${pageName(pathname)}` });
}

let _wired = false;
export function initAnalytics() {
  if (_wired || typeof window === "undefined") return;
  _wired = true;
  window.addEventListener("beforeunload", () => flush(true));
  document.addEventListener("visibilitychange", () => { if (document.visibilityState === "hidden") flush(true); });
}
